import type { HttpMethod, PathsWithMethod } from 'openapi-typescript-helpers';

import { GLOSS, settings, VORTEX_ACCOUNTS } from '@/constants/settings';
import { getGlossDataQuery, getGlossVersionQuery } from '@/gql/queries';
import type { GlossaryQuery } from '@/types/gloss';
import type { paths } from '@/types/api';

import { useOpenApiClient } from '@/models/openapi-client';

import type { DogtagResponse } from '@/models/dogtag-store';

import type { AsyncDataOptions } from '@/shared/model/types/nuxt-schema';

import type {
  UploadReplayResponse,
  BaseError,
  ReplayDeleteByIdParams,
  EditReplayResponse,
} from '@/shared/model/types/api-types';

import type { Resources } from '@/composables/data-provider';
import type { Clan, ClanDataResponse } from '@/types/vortex';

export type GlossaryResponse = {
  data: GlossaryQuery;
};

type ResourceType = keyof Resources;

type Paths<M extends HttpMethod> = PathsWithMethod<paths, M>;

type GlossaryResponseVersion = {
  data: Pick<GlossaryQuery, 'version'>;
};

const GLOSSARY_FULL_URL = settings.glossUrl + GLOSS;
const VORTEX_ACCOUNTS_FULL_URL = settings.glossUrl + VORTEX_ACCOUNTS;

type PathKeys = keyof paths;

type ApiResourseOption = {
  path: PathKeys;
  method: HttpMethod;
};

const resourcePathMap = new Map<keyof Resources, ApiResourseOption>([
  ['homePage', { path: '/api/public/v1/index/all', method: 'get' }],

  ['replayById', { path: '/api/public/v1/replays/{replay_id}', method: 'get' }],
  ['replayDeleteById', { path: '/api/public/v1/replays/{replay_id}/delete', method: 'delete' }],
  ['replayUpload', { path: '/api/public/v1/replays/upload', method: 'post' }],
  ['replayEdit', { path: '/api/public/v1/replays/{replay_id}/edit', method: 'post' }],

  ['filter', { path: '/api/public/v1/replays/filter', method: 'get' }],
  ['filterAll', { path: '/api/public/v1/filters/all', method: 'get' }],
  ['hashtagsAll', { path: '/api/public/v1/hashtags/all', method: 'get' }],
  ['actualVideos', { path: '/api/public/v1/index/actual_videos', method: 'get' }],

  ['account', { path: '/api/public/v1/replays/account/{spa_id}', method: 'get' }],
  ['contestAll', { path: '/api/public/v1/contests/all', method: 'get' }],
  ['contest', { path: '/api/public/v1/contests/{contest_id}', method: 'get' }],
]);

const defaultOptions: AsyncDataOptions<unknown> = {
  immediate: true,
  lazy: true,
};

export const useNetworkManager = <Parameters, ExpectedData, ExpectedError = BaseError>(
  r: ResourceType,
  parameters?: Parameters,
  options?: AsyncDataOptions<ExpectedData>
) => {
  const optionsData = { ...defaultOptions, ...options } as AsyncDataOptions<ExpectedData>;
  const { client: apiClient } = useOpenApiClient();
  const resource = resourcePathMap.get(r);
  if (!resource) {
    throw new Error('failed request resource');
  }

  return useAsyncData<ExpectedData, ExpectedError>(
    `${r}:${encodeURIComponent(JSON.stringify(parameters))}`,
    async () => {
      switch (resource.method) {
        case 'get': {
          const { data, error } = await apiClient.GET(
            resource.path as Paths<typeof resource.method>,
            {
              params: parameters || {},
            }
          );

          if (error) {
            const errorMsg =
              'client_msg' in error ? (error['client_msg'] as string) : 'Error fetching data';
            throw new Error(errorMsg);
          }

          return data as ExpectedData;
        }

        case 'delete': {
          const { data, error } = await apiClient.DELETE(
            resource.path as Paths<'delete'>,
            { params: parameters } as any
          );
          if (error) {
            throw error;
          }

          return data as ExpectedData;
        }

        default: {
          throw new Error('Unsupported method');
        }
      }
    },
    optionsData as any
  );
};

export const fetchDogtagComponentsByAccountIds = (accountList: Ref<string[]>) =>
  useFetch<DogtagResponse>(`${settings.glossUrl}/api/dogtags/`, {
    body: { account_ids: accountList.value },
    method: 'post',
    watch: [accountList],
    immediate: false,
  });

export const fetchClanInfoByAccountId = async (
  accountId: number | string
): Promise<Clan | undefined> => {
  try {
    const url = `${VORTEX_ACCOUNTS_FULL_URL}${accountId}/clans/`;
    const { data, status } = await $fetch<ClanDataResponse>(url);

    if (status !== 'ok' || !data.clan) return;

    return data.clan;
  } catch {
    return undefined;
  }
};

export const getGlossaryVersion = async (): Promise<string | null | undefined> => {
  const { data } = await $fetch<GlossaryResponseVersion>(GLOSSARY_FULL_URL, {
    method: 'POST',
    body: JSON.stringify({
      variables: {
        lang: 'ru',
      },
      query: getGlossVersionQuery,
    }),
  });

  return data.version;
};

export const getGlossaryData = () =>
  useFetch<GlossaryResponse>(GLOSSARY_FULL_URL, {
    method: 'POST',

    body: JSON.stringify({
      variables: {
        lang: 'ru',
      },
      query: getGlossDataQuery,
    }),
  });

/**
 * For upload or edit replay requests.
 */
export const useUploadReplay = (formData: Ref<FormData | null>, replayId?: number) => {
  const config = useRuntimeConfig();

  const { data, status, error, execute } = useAsyncData<
    UploadReplayResponse | EditReplayResponse,
    BaseError
  >(
    'upload-replay',
    () => {
      const isNewReplay = formData.value?.has('replay');
      const resourceKey: keyof Resources = isNewReplay ? 'replayUpload' : 'replayEdit';
      const resource = resourcePathMap.get(resourceKey);

      let reqUrl = `${config.public.apiEndpoint}${resource?.path}`;
      if (!isNewReplay) {
        reqUrl = reqUrl.replace('{replay_id}', replayId?.toString() ?? '');
      }

      return $fetch<UploadReplayResponse | EditReplayResponse>(reqUrl, {
        method: 'POST',
        body: formData.value,
        credentials: 'include',
        retry: 3,
        retryDelay: 1500,
      });
    },
    { immediate: false }
  );

  return {
    execute,
    status,
    data,
    error,
  };
};

export const requestDeleteReplay = (replayId: ReplayDeleteByIdParams['path']['replay_id']) => {
  const { client: apiClient } = useOpenApiClient();

  return apiClient.DELETE('/api/public/v1/replays/{replay_id}/delete', {
    params: { path: { replay_id: replayId } },
  });
};
